import { useCloudApi } from '@/api/cloudApi'
import type {
  V1alpha1PoolOption,
  CloudV1alpha1PoolRef,
  CloudV1alpha1Organization,
  V1alpha1PoolMemberEnvironment
} from '@streamnative/cloud-api-client-typescript'
import type { AxiosError } from 'axios'
import { i18n } from '@/lang'
import type { PricePackage } from '@/data/pricePackages'
import { useMemoize } from '@vueuse/core'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
const { t } = i18n.global
const billingQueryStrategy = 'annotations.cloud.streamnative.io/billing-query-strategy'
export type DeploymentType = Exclude<keyof PricePackage, 'flink'>
export type PoolDeploymentTypeV2 = 'byoc' | 'byoc-pro' | 'dedicated' | 'dedicated-pro'
export type PoolDeploymentTypeV2Label = 'BYOC' | 'BYOC-Pro' | 'Dedicated' | 'Dedicated-Pro'

export const fetchOrganization = async (namespace: string | undefined) => {
  if (namespace === undefined) {
    throw Error(getErrorMessage('getOrganization error'))
  }

  try {
    const api = useCloudApi()
    return await api.readOrganization(namespace)
  } catch (e) {
    if ((e as AxiosError).response?.status === 403) {
      // When organization is not ready, it also throws 403.
      throw Error(t(`organization.notification.notReady`))
    }
    throw Error(getErrorMessage(e, 'getOrganization error'))
  }
}

export const fetchOrganizationMemo = useMemoize(fetchOrganization)

export const updateOrganization = async (
  organization: CloudV1alpha1Organization,
  data: { displayName: string; billingEmail: string; operatorEmail: string }
) => {
  const api = useCloudApi()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patchArray: Record<string, any> = []
  if (data.displayName) {
    patchArray.push({
      op: 'replace',
      path: `/spec/displayName`,
      value: data.displayName
    })
  } else if (organization.spec?.displayName) {
    patchArray.push({ op: 'remove', path: `/spec/displayName` })
  }
  if (data.billingEmail) {
    patchArray.push({ op: 'replace', path: `/spec/billingAccount/email`, value: data.billingEmail })
  } else {
    throw Error('billingEmail is required')
  }
  if (data.operatorEmail) {
    patchArray.push({ op: 'replace', path: `/spec/operatorEmail`, value: data.operatorEmail })
  } else if (organization.spec?.operatorEmail) {
    patchArray.push({ op: 'remove', path: `/spec/operatorEmail` })
  }
  return await api.patchOrganization(organization.metadata?.name as string, patchArray)
}

/**
 * Set or update an annotation on Organization metadata.
 */
export const setOrganizationAnnotation = async (
  organizationName: string,
  key: string,
  value: string
) => {
  const api = useCloudApi()
  const { data: org } = await api.readOrganization(organizationName)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patches: Array<Record<string, any>> = []

  if (!org.metadata?.annotations) {
    patches.push({ op: 'add', path: '/metadata/annotations', value: {} })
  }
  // JSON Patch uses JSON Pointer for paths (RFC 6901). In JSON Pointer, '/' is a path separator
  // and must be escaped as '~1' (and '~' would be escaped as '~0' if needed).
  // Here we escape '/' in annotation keys so it can be used safely in the patch path.
  const escapedKey = key.replace(/\//g, '~1')
  patches.push({
    op: 'add',
    path: `/metadata/annotations/${escapedKey}`,
    value
  })

  return await api.patchOrganization(organizationName, patches)
}

/**
 * Remove an annotation from Organization metadata if it exists.
 */
export const removeOrganizationAnnotation = async (organizationName: string, key: string) => {
  const api = useCloudApi()
  const { data: org } = await api.readOrganization(organizationName)
  const current = org.metadata?.annotations || {}
  if (!Object.prototype.hasOwnProperty.call(current, key)) {
    return org
  }
  // JSON Patch uses JSON Pointer for paths (RFC 6901). In JSON Pointer, '/' is a path separator
  // and must be escaped as '~1' (and '~' would be escaped as '~0' if needed).
  // Here we escape '/' in annotation keys so it can be used safely in the patch path.
  const escapedKey = key.replace(/\//g, '~1')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patches: Array<Record<string, any>> = [
    { op: 'remove', path: `/metadata/annotations/${escapedKey}` }
  ]
  return await api.patchOrganization(organizationName, patches)
}

export interface Pool {
  cloudType: string
  name: string
  namespace: string
  label: string
  poolRef: CloudV1alpha1PoolRef
  deploymentType: DeploymentType
  deploymentTypeV2: PoolDeploymentTypeV2
  deploymentTypeLabel: PoolDeploymentTypeV2Label
  features?: { [key: string]: boolean }
  environments: Array<V1alpha1PoolMemberEnvironment>
}

export const getDeploymentType = (poolOption: V1alpha1PoolOption): DeploymentType => {
  if (poolOption.metadata?.annotations?.[billingQueryStrategy]) {
    return poolOption.metadata?.annotations?.[billingQueryStrategy] as DeploymentType
  }
  if (poolOption.spec?.deploymentType) {
    return poolOption.spec.deploymentType as DeploymentType
  }
  if (poolOption.spec?.poolRef.namespace === 'streamnative') {
    return 'hosted'
  }
  return 'managed'
}

export const getPoolDeploymentTypeLabel = (
  pdt: PoolDeploymentTypeV2
): PoolDeploymentTypeV2Label => {
  switch (pdt) {
    case 'byoc':
      return 'BYOC'
    case 'byoc-pro':
      return 'BYOC-Pro'
    case 'dedicated':
      return 'Dedicated'
    case 'dedicated-pro':
      return 'Dedicated-Pro'
    default:
      throw Error(`invalid pool deployment type ${pdt}`)
  }
}
